import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SKIP_NOTIFICATION } from '~core/interceptors/error-handler.interceptor';
import { environment } from 'src/environments/environment';
import { ChangeStatusData, EffectStatusData } from '~shared/types/websockets.types';

export interface ProjectStatusResponse {
	projectId: string;
	changes: ChangeResponse[];
	hasPendingEffects: boolean;
}

export interface ChangeResponse {
	correlationId: string;
	description?: string;
	identifier: Record<string, string>;
	originator: string;
	initiatedBy?: string;
	createdAt: string;
	effects: EffectStatusData[];
	pending: number;
	inProgress: number;
	completed: number;
	failed: number;
}

@Injectable({ providedIn: 'root' })
export class EffectStatusService {
	static readonly IMAGE_EFFECT_TYPES = ['STORE_ITEM_PRODUCT_IMAGE', 'STORE_PART_PRODUCT_IMAGE'];

	private baseUrl = environment.changetrackerServiceUrl + 'manifest';

	constructor(private http: HttpClient) {}

	getProjectStatus(projectId: string): Observable<ProjectStatusResponse> {
		return this.http.get<ProjectStatusResponse>(`${this.baseUrl}/project/${projectId}/status`, {
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
		});
	}

	deleteChange(correlationId: string): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/change/${correlationId}`, {
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
		});
	}

	deleteAllChanges(projectId: string): Observable<{ deleted: number }> {
		return this.http.delete<{ deleted: number }>(`${this.baseUrl}/project/${projectId}/changes`, {
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
		});
	}

	getPendingChanges(projectId: string): Observable<ChangeStatusData[]> {
		return this.http.get<ProjectStatusResponse>(`${this.baseUrl}/project/${projectId}/status`, {
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
			params: { status: ['PENDING', 'IN_PROGRESS'] },
		}).pipe(map(status => status.changes.map(EffectStatusService.toChangeStatus)));
	}

	/**
	 * The changes the header shows: those with work still going, plus those with a
	 * failed image. A failed image stays on show until a later render of the same item
	 * or part supersedes it; other failures keep showing only while their change is live.
	 */
	getShownChanges(projectId: string): Observable<ChangeStatusData[]> {
		return this.http.get<ProjectStatusResponse>(`${this.baseUrl}/project/${projectId}/status`, {
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
			params: { status: ['PENDING', 'IN_PROGRESS', 'FAILED'] },
		}).pipe(
			map(status => status.changes
				.filter(change => change.effects.some(effect =>
					effect.status === 'PENDING' || effect.status === 'IN_PROGRESS' || EffectStatusService.isFailedImage(effect)))
				.map(EffectStatusService.toChangeStatus))
		);
	}

	static isFailedImage(effect: EffectStatusData): boolean {
		return effect.status === 'FAILED' && EffectStatusService.IMAGE_EFFECT_TYPES.includes(effect.effectType);
	}

	static toChangeStatus(change: ChangeResponse): ChangeStatusData {
		return {
			correlationId: change.correlationId,
			description: change.description,
			originator: change.originator,
			identifier: change.identifier,
			createdAt: change.createdAt,
			effects: change.effects,
		};
	}
}
